import {
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CompanySSOSettingsForm, SsoUserGroup } from './edit-company-sso.types';
import { FileDropzone, FileDropzoneStatus } from '../../../../../../../redesign';

const SAML_METADATA_MAX_SIZE_BYTES = 1024 * 1024;
const SAML_METADATA_ACCEPT = {
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
};

const metadataFieldName = {
  [SsoUserGroup.OGP]: 'ogpMetadataFile',
  [SsoUserGroup.WORKER]: 'workerMetadataFile',
} as const;

type SsoSamlConfigurationProps = {
  ssoUserGroup: SsoUserGroup;
  watch: UseFormWatch<CompanySSOSettingsForm>;
  setValue: UseFormSetValue<CompanySSOSettingsForm>;
  hasError?: boolean;
  setError: UseFormSetError<CompanySSOSettingsForm>;
  clearErrors: UseFormClearErrors<CompanySSOSettingsForm>;
};

export const SsoSamlConfiguration = ({
  ssoUserGroup,
  watch,
  setValue,
  clearErrors,
  hasError,
  setError,
}: SsoSamlConfigurationProps) => {
  const { t } = useTranslation('ogp-screen-specific');
  const fieldName = metadataFieldName[ssoUserGroup];
  const metadataFile = watch(fieldName);

  return (
    <>
      <Typography variant="h3" sx={{ mb: 4 }}>
        {t('edit-sso-settings.saml-configuration.title')}
      </Typography>
      <FileDropzone
        files={metadataFile ? [{ file: metadataFile, status: FileDropzoneStatus.Uploaded }] : []}
        onFilesSelected={(files) => {
          clearErrors(fieldName);
          setValue(fieldName, files[0] ?? null);
        }}
        onRemoveFile={() => {
          clearErrors(fieldName);
          setValue(fieldName, null);
        }}
        onValidityChange={(isValid) => {
          if (isValid) {
            clearErrors(fieldName);
          } else {
            setError(fieldName, {
              type: 'validate',
              message: t('edit-sso-settings.file-drop.error.text'),
            });
            setValue(fieldName, null);
          }
        }}
        title={t('edit-sso-settings.file-drop.title')}
        hint={t('edit-sso-settings.file-drop.hint')}
        labels={{
          browse: t('edit-sso-settings.file-drop.browse.label'),
          uploaded: t('edit-sso-settings.file-drop.uploaded.label'),
          remove: t('edit-sso-settings.file-drop.remove.label'),
          cancel: t('edit-sso-settings.file-drop.cancel.label'),
          download: t('edit-sso-settings.file-drop.download.label'),
        }}
        invalidFileMessage={t('edit-sso-settings.file-drop.error.text')}
        accept={SAML_METADATA_ACCEPT}
        maxSize={SAML_METADATA_MAX_SIZE_BYTES}
        hasError={hasError}
        data-testid={`${ssoUserGroup.toLowerCase()}-sso-file-dropzone`}
      />
    </>
  );
};
